import APopover from 'components/APopover';
import Button from 'components/Button';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes, css } from 'styled-components';
import LocalIcon from 'ui/LocalIcon';
import {
  black,
  blue,
  border1,
  exactWhite,
  gray,
  red,
} from 'utils/style/colors';
import ProgressBar from 'components/ProgressBar/ProgressBar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { ConvertMeetingVoiceToText } from 'api/ManApi';
import { convertDigitToProperFormat } from 'utils/number';
import Input from 'components/Input/Input';
import {
  AI_GPT_MODEL_OPTIONS,
  DEFAULT_AI_GPT_MODEL,
} from 'api/chatbot-api/aiModels';
import i18n from 'i18n';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
    height: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    height: 3.5rem;
  }
`;

const VoiceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  cursor: pointer;
  color: ${blue};
  border-radius: 0.75rem;
  padding: 0.25rem 0.5rem;
  transition: all 0.25s ease-in-out;

  &:hover {
    color: var(--exact-white);
    background: ${blue};

    & div {
      background-color: var(--exact-white);
    }
  }
`;

const VoicePopupContainer = styled.form`
  background: var(--exact-white);
  width: 30rem;
  max-width: 30rem;
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FileInput = styled.input`
  display: none;
`;

const SelectVoiceFileBtn = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  flex: 1;
  padding: 0.5rem 1rem;
  border: 2px dashed ${blue};
  border-radius: 0.5rem;
  cursor: pointer;
  color: ${blue};
  transition: all 0.25s ease-in-out;
  font-size: 0.875rem;

  & > p {
    padding-top: 0.25rem;
  }

  &:hover {
    background-color: ${blue};
    color: var(--exact-white);
    border-color: ${blue};
    & div {
      background-color: var(--exact-white);
    }
  }
`;

const UploadedItem = styled.div<{ isEnable: boolean }>`
  display: ${({ isEnable }) => (isEnable ? 'flex' : 'none')};
  align-items: stretch;
  justify-content: space-between;
  border-radius: 0.5rem;
  height: auto;
  gap: 0.5rem;
  overflow: hidden;
  border: ${border1};
  flex: 1;
  animation: ${({ isEnable }) =>
    isEnable
      ? css`
          ${fadeIn} 0.5s ease-in-out forwards
        `
      : 'none'};
`;

const FileInfo = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  height: 100%;
  min-width: 0;
  overflow: hidden;
`;

const FileName = styled.span`
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.5);
  margin-inline-start: -4rem;
  padding-inline-start: 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
`;

const DeleteContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: #f5f5f5;
  border-radius: 0.5rem;
`;

const ProgressInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.25rem;
  font-weight: bold;
`;

const ProgressText = styled.span`
  font-size: 0.75rem;
  color: #666;
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
  color: ${gray};

  &:hover {
    background-color: rgba(239, 77, 62, 0.1);
    color: ${red};
  }
`;

const BadgeContainer = styled.div`
  height: 100%;
  width: 7rem;
  background-image: ${(props) =>
    props.theme.direction === 'rtl'
      ? `linear-gradient(to left, ${props.color} 0%, var(--exact-white) 90%)`
      : `linear-gradient(to right, ${props.color} 0%, var(--exact-white) 90%)`};
  display: flex;
  align-items: center;
  padding-inline-start: 0.5rem;
`;

const VoiceToTextDescription = styled.div`
  width: 100%;
  margin: 0.5rem 0;
  padding: 0.5rem;
  border-radius: 0.5rem;
  font-weight: bold;
  text-wrap: auto;
`;

const FileSelectRow = styled.div<{ alignEnd?: boolean }>`
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  justify-content: space-between;
`;

const AiSettingsPopoverContent = styled.div`
  background: var(--exact-white);
  border-radius: 0.5rem;
  width: 17.5rem;
  max-width: 17.5rem;
`;

const AiSettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5rem 0.5rem 0;
`;

const AiSettingsCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(239, 77, 62, 0.1);
  }
`;

const AiSettingsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border: 2px dashed ${blue};
  border-radius: 0.5rem;
  cursor: pointer;
  background: transparent;
  color: ${blue};
  transition: all 0.25s ease-in-out;
  flex-shrink: 0;

  &:hover {
    background-color: ${blue};
    color: var(--exact-white);

    & div {
      background-color: var(--exact-white);
    }
  }

  &:active {
    background: ${blue};
    color: ${exactWhite};
  }
`;

const AiSettingsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 16rem;
  padding: 0 0.75rem 0.75rem;
`;

const AiSettingsLabel = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${black};
`;

const UserPromptTextarea = styled.textarea`
  width: 100%;
  min-height: 4rem;
  max-height: 8rem;
  border: ${border1};
  border-radius: 0.4375rem;
  padding: 0.3125rem;
  outline: none;
  resize: vertical;
  font-size: 0.875rem;
  font-family: inherit;
`;

const AiModelField = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  border: none;
  margin: 0;
  padding: 0;
`;

const AiModelLabel = styled.legend`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${black};
  padding: 0;
`;

const AiModelRadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  align-items: center;
`;

interface IVoiceToTextProps {
  onFillText: (text: string) => void;
}

const VoiceToText: React.FC<IVoiceToTextProps> = ({ onFillText }) => {
  const { t } = useTranslation('general');

  const store = useSelector((state: RootState) => state);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [btnText, setBtnText] = useState<string>(t('convertVoiceToText'));
  const [userPrompt, setUserPrompt] = useState<string>('');
  const [aiGptModel, setAiGptModel] = useState<string>(DEFAULT_AI_GPT_MODEL);
  const [isAiSettingsOpen, setIsAiSettingsOpen] = useState<boolean>(false);
  const abortController = useRef<any>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith('audio/')) {
        setSelectedFile(file);
        setUploadProgress(0);
      } else {
        alert(t('pleaseSelectAudioFile') || 'Please select an audio file');
      }
    }
  };

  useEffect(() => {
    if (uploadProgress === 100 && isUploading) {
      setBtnText(t('Processing'));
    } else {
      setBtnText(t('convertVoiceToText') || 'Converting...');
    }
  }, [uploadProgress, isUploading]);

  const handleDeleteFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancelUpload = () => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }
    setIsUploading(false);
    setUploadProgress(0);
  };

  const handleSubmitForm = async (event: FormEvent) => {
    event.preventDefault();

    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('voice', selectedFile);
    formData.append('meetingNoteModel', aiGptModel);
    if (userPrompt.trim()) {
      formData.append('userPrompt', userPrompt.trim());
    }

    const controller = new AbortController();
    abortController.current = controller;

    try {
      const res = await ConvertMeetingVoiceToText(
        store,
        formData,
        setUploadProgress,
        controller
      );
      console.log(res);
      onFillText(res.data.result || '');
      setUploadProgress(100);
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log('Upload canceled');
      } else {
        console.error('Upload failed:', error);
        alert(t('uploadFailed') || 'Upload failed');
      }
    } finally {
      setIsUploading(false);
      abortController.current = null;
    }
  };

  const aiSettingsPopover = (
    <APopover
      marginToTarget={5}
      placement="bottom-end"
      trigger="click"
      viewportFit
      show={isAiSettingsOpen}
      onShowChange={setIsAiSettingsOpen}
      target={
        <AiSettingsButton type="button" disabled={isUploading}>
          <LocalIcon iconName="setting.svg" color={blue} type="sm" />
        </AiSettingsButton>
      }
    >
      <AiSettingsPopoverContent>
        <AiSettingsHeader>
          <AiSettingsCloseButton
            type="button"
            aria-label={t('cancel')}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              setIsAiSettingsOpen(false);
            }}
          >
            <LocalIcon
              iconName="x.svg"
              color={gray}
              type="sm"
              hover={true}
              fillHoverColor={red}
            />
          </AiSettingsCloseButton>
        </AiSettingsHeader>
        <AiSettingsPanel>
          <div className="flex flex-col gap-2">
            <AiSettingsLabel htmlFor="voice-to-text-user-prompt">
              {i18n.t('userPrompt', { ns: 'survey' }) as string}
            </AiSettingsLabel>
            <UserPromptTextarea
              id="voice-to-text-user-prompt"
              value={userPrompt}
              disabled={isUploading}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                setUserPrompt(event.target.value)
              }
              placeholder={
                i18n.t('userPromptDefault', {
                  ns: 'survey',
                }) as string
              }
            />
          </div>

          <AiModelField>
            <AiModelLabel>
              {i18n.t('aiGptModel', { ns: 'survey' }) as string}
            </AiModelLabel>
            <AiModelRadioGroup>
              {AI_GPT_MODEL_OPTIONS.map((option) => (
                <Input
                  key={option.value}
                  id={`voice-to-text-model-${option.value}`}
                  type="radio"
                  name="voiceToTextAiGptModel"
                  checked={aiGptModel === option.value}
                  label={option.label}
                  disabled={isUploading}
                  onChange={() => setAiGptModel(option.value)}
                />
              ))}
            </AiModelRadioGroup>
          </AiModelField>
        </AiSettingsPanel>
      </AiSettingsPopoverContent>
    </APopover>
  );
  const [isOpen, setIsOpen] = useState(false);
  return (
    <APopover
      target={
        <VoiceContainer>
          <LocalIcon iconName="microphone.svg" color={blue} type="sm" />
          <p>{t('voiceToText')}</p>
        </VoiceContainer>
      }
      show={isOpen}
      onShowChange={(show) => {
        setIsAiSettingsOpen(false);
        setIsOpen(show);
      }}
    >
      <VoicePopupContainer onSubmit={handleSubmitForm}>
        <VoiceToTextDescription>
          {t('DescriptionForConvertVoiceToText')}
        </VoiceToTextDescription>

        {isUploading && (
          <ProgressContainer>
            <ProgressInfo>
              <ProgressText>
                {uploadProgress < 100
                  ? `${t('uploading') || 'Uploading'}... ${convertDigitToProperFormat(uploadProgress)}%`
                  : `${t('processingVoiceToText') || 'Processing voice to text'}...`}
              </ProgressText>
              <ProgressBar
                color={`${blue}`}
                total={100}
                count={uploadProgress}
              />
            </ProgressInfo>
            <CancelButton type="button">
              <LocalIcon
                iconName="x.svg"
                color={gray}
                type="sm"
                isLink={true}
                onClick={handleCancelUpload}
              />
            </CancelButton>
          </ProgressContainer>
        )}

        {!isUploading && (
          <>
            <FileInput
              ref={fileInputRef}
              type="file"
              accept=".mp3, .m4a, audio/mpeg, audio/mp4"
              onChange={handleFileSelect}
              id="voice-file-input"
            />

            <FileSelectRow alignEnd={!!selectedFile}>
              {!selectedFile && (
                <SelectVoiceFileBtn htmlFor="voice-file-input">
                  <LocalIcon iconName="microphone.svg" color={blue} type="sm" />
                  <p>{t('selectVoiceFile')}</p>
                </SelectVoiceFileBtn>
              )}

              <UploadedItem isEnable={selectedFile && !isUploading}>
                <FileInfo>
                  <BadgeContainer color={blue}>
                    <LocalIcon
                      iconName="music-square.svg"
                      color={'var(--exact-white)'}
                      type="sm"
                    />
                  </BadgeContainer>
                  <FileName>{selectedFile?.name}</FileName>
                </FileInfo>
                <DeleteContainer type="button" onClick={handleDeleteFile}>
                  <LocalIcon
                    iconName="x.svg"
                    color={gray}
                    type="sm"
                    hover={true}
                    fillHoverColor={red}
                  />
                </DeleteContainer>
              </UploadedItem>

              {aiSettingsPopover}
            </FileSelectRow>
          </>
        )}

        <Button
          type="submit"
          disabled={!selectedFile || isUploading}
          color="blue-outline"
          style={{ borderRadius: '0.5rem' }}
        >
          {btnText}
        </Button>
      </VoicePopupContainer>
    </APopover>
  );
};

export default VoiceToText;
